import React, { useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { List } from '@common-fe/common-fe';

import useLinkedEmployeesQuery from '@/modules/employee/Employee/RegistrationAndLogin/LinkedAccounts/useLinkedEmployees.query';
import LoginHistoryList from '@/modules/LoginHistory/LoginHistoryList';

import useEmployeeLoginHistoryParams from '../hooks/useEmployeeLoginHistoryParams';
import useGetEmployeeLoginHistoryQuery from '../queries/useGetEmployeeLoginHistory.query';

const EmployeeLoginHistoryList = () => {
  const { params: { id } } = useRouteMatch<{ id: string }>();
  const {
    formattedData: { linkedEmployees: linkedAccounts, primaryEmployeeId },
    isLoading: isGettingLinkedIds,
  } = useLinkedEmployeesQuery(id);
  const { page, setPage, perPage, setPerPage, startDate, endDate, setPeriod } = useEmployeeLoginHistoryParams();

  const employeeIds = useMemo(() => {
    const linkedAccountsIds = linkedAccounts?.map((linkedAccount) => linkedAccount?.employeeId) || [];
    return [...new Set([id, primaryEmployeeId, ...linkedAccountsIds])].filter(Boolean) as string[];
  }, [id, linkedAccounts, primaryEmployeeId]);

  const { employeeLoginHistory, isLoading, total } = useGetEmployeeLoginHistoryQuery({
    employeeIds,
    startDate,
    endDate,
    page,
    perPage,
    enabled: !isGettingLinkedIds,
  });

  const hasDateRangeFilter = useMemo(() => startDate || endDate, [startDate, endDate]);

  return (
    <LoginHistoryList
      loginHistory={employeeLoginHistory}
      isLoading={isLoading || isGettingLinkedIds}
      total={total}
      page={page}
      setPage={setPage}
      perPage={perPage}
      setPerPage={setPerPage}
      startDate={startDate}
      endDate={endDate}
      setPeriod={setPeriod}
      placeholder={(
        <List.Placeholder
          testId="login-history-list_placeholder"
          title="There are no authentication history records"
          {...hasDateRangeFilter ? { text: null } : { text: "This employee hasn't logged in yet, or they use SSO, which cannot be tracked" }}
          height="fit-content"
          hideIcon
        />
      )}
    />
  );
};

export default EmployeeLoginHistoryList;
